// src/pages/TalleresCrear/components/ModalidadesPago.jsx
import React from 'react';
import { Form, Checkbox, InputNumber, Card, Space, Tag, Alert, Typography, Tooltip } from 'antd';
import { InfoCircleOutlined, WarningOutlined, LockOutlined } from '@ant-design/icons';
import { PLANES } from '../constants';
import { formatCLP, parseCLP } from '../utils';

const { Text } = Typography;

// Campos de valores SIN matrícula (la matrícula ahora es global del recinto)
const CAMPOS_VALORES = [
  { 
    key: 'valorParticular', 
    label: 'Particular', 
    required: true,
    description: 'Precio base para público general'
  },
  { 
    key: 'valorVecino', 
    label: 'Vecino', 
    required: false,
    description: 'Precio especial para vecinos de Ñuñoa'
  },
  { 
    key: 'valorAdultoMayor', 
    label: 'Adulto Mayor', 
    required: false,
    description: 'Precio especial para adultos mayores'
  },
  { 
    key: 'valorFuncionarios', 
    label: 'Funcionarios', 
    required: false,
    description: 'Precio especial para funcionarios municipales'
  }
];

export default function ModalidadesPago({ planesPermitidos = ['mensual', 'trimestral', 'semestral', 'anual'] }) {
  const form = Form.useFormInstance();

  // 🆕 Función para manejar toggle con limpieza automática de planes no permitidos
  const handlePlanToggle = (planKey, checked) => {
    if (!checked) {
      // Si se desactiva, limpiar valores
      const modalidades = form.getFieldValue('modalidades') || {};
      modalidades[planKey] = {
        activo: false,
        valorParticular: null,
        valorVecino: null,
        valorAdultoMayor: null,
        valorFuncionarios: null
      };
      form.setFieldsValue({ modalidades });
    }
  };

  // 🆕 Obtener información del plan según duración mínima
  const getPlanInfo = (planValue) => {
    const infoMap = {
      'mensual': { diasMinimos: 30, meses: 1 },
      'trimestral': { diasMinimos: 90, meses: 3 },
      'semestral': { diasMinimos: 180, meses: 6 },
      'anual': { diasMinimos: 365, meses: 12 }
    };
    return infoMap[planValue] || { diasMinimos: 30, meses: 1 };
  };

  return (
    <div className="space-y-3">
      {/* Alert informativa principal */}
      <Alert
        message="Configura los planes de pago"
        description={
          <div className="space-y-2">
            <p>Selecciona los planes disponibles y define sus precios. El valor particular es obligatorio para cada plan seleccionado.</p>
            <p className="text-xs font-medium mt-2">
              ℹ️ La matrícula del recinto (si existe) se aplicará automáticamente según la configuración global del recinto.
            </p>
          </div>
        }
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
        className="mb-4"
      />

      {PLANES.map((plan) => {
        // 🆕 Verificar si el plan está permitido
        const planPermitido = planesPermitidos.includes(plan.value);
        const planInfo = getPlanInfo(plan.value);

        return (
          <Card
            key={plan.value}
            size="small"
            title={
              <Space>
                <Form.Item
                  name={['modalidades', plan.value, 'activo']}
                  valuePropName="checked"
                  noStyle
                >
                  <Checkbox 
                    disabled={!planPermitido}
                    onChange={(e) => handlePlanToggle(plan.value, e.target.checked)}
                  >
                    <span className="font-medium">{plan.label}</span>
                  </Checkbox>
                </Form.Item>
                {/* 🆕 Ícono de candado para planes no permitidos */}
                {!planPermitido && (
                  <Tooltip title={`Este plan requiere que el taller dure al menos ${planInfo.diasMinimos} días (${planInfo.meses} meses)`}>
                    <LockOutlined style={{ color: '#999', fontSize: '14px' }} />
                  </Tooltip>
                )}
              </Space>
            }
            className={!planPermitido ? 'opacity-60' : ''}
          >
            {/* 🆕 Alerta para planes no permitidos */}
            {!planPermitido && (
              <Alert
                message="Plan no disponible para este taller"
                description={
                  <div className="text-xs">
                    <p>Este plan requiere que el taller tenga una duración de al menos <strong>{planInfo.diasMinimos} días ({planInfo.meses} meses)</strong>.</p>
                    <p className="mt-1">Para habilitarlo, ajusta las fechas de actividades en el paso anterior o elige un plan más corto.</p>
                  </div>
                }
                type="warning"
                showIcon
                banner
                className="mb-3"
              />
            )}

            <Form.Item noStyle shouldUpdate={(prev, curr) => 
              prev.modalidades?.[plan.value]?.activo !== curr.modalidades?.[plan.value]?.activo
            }>
              {({ getFieldValue }) => {
                const activo = getFieldValue(['modalidades', plan.value, 'activo']);
                
                if (!activo) {
                  return (
                    <Text type="secondary" className="text-sm block py-2">
                      {planPermitido 
                        ? 'Marca el checkbox para configurar precios de este plan'
                        : 'Este plan no está disponible debido a la duración del taller'
                      }
                    </Text>
                  );
                }

                return (
                  <div className="space-y-3 mt-3">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {CAMPOS_VALORES.map((campo) => (
                        <Form.Item
                          key={campo.key}
                          label={
                            <Space size={4}>
                              <span title={campo.description}>
                                {campo.label}
                              </span>
                              {campo.required && <Tag color="red" size="small">*</Tag>}
                            </Space>
                          }
                          name={['modalidades', plan.value, campo.key]}
                          rules={campo.required ? [
                            { required: true, message: 'Requerido' },
                            { 
                              validator: (_, value) => {
                                if (value && value <= 0) {
                                  return Promise.reject('Debe ser mayor a 0');
                                }
                                return Promise.resolve();
                              }
                            }
                          ] : [
                            { 
                              validator: (_, value) => {
                                if (value && value < 0) {
                                  return Promise.reject('No puede ser negativo');
                                }
                                return Promise.resolve();
                              }
                            }
                          ]}
                          className="mb-0"
                          tooltip={campo.description}
                        >
                          <InputNumber
                            size="middle"
                            className="w-full"
                            min={0}
                            formatter={formatCLP}
                            parser={parseCLP}
                            prefix="$"
                            placeholder="0"
                          />
                        </Form.Item>
                      ))}
                    </div>

                    {/* Info adicional sobre precios */}
                    <div className="mt-2 p-2 bg-gray-50 rounded">
                      <Text type="secondary" className="text-xs">
                        💡 <strong>Tip:</strong> Los valores opcionales (Vecino, Adulto Mayor, Funcionarios) solo se mostrarán si son menores al precio particular.
                      </Text>
                    </div>
                  </div>
                );
              }}
            </Form.Item>
          </Card>
        );
      })}

      {/* Validación: al menos un plan seleccionado */}
      <Form.Item noStyle shouldUpdate>
        {({ getFieldValue }) => {
          const modalidades = getFieldValue('modalidades');
          if (!modalidades) return null;

          const planesActivos = Object.values(modalidades).filter(m => m?.activo).length;

          if (planesActivos === 0) {
            return (
              <Alert
                message="Selecciona al menos un plan"
                description="Debes configurar al menos una modalidad de pago para continuar"
                type="warning"
                showIcon
                icon={<WarningOutlined />}
              />
            );
          }

          return (
            <Alert
              message={`${planesActivos} plan${planesActivos > 1 ? 'es' : ''} configurado${planesActivos > 1 ? 's' : ''}`}
              type="success"
              showIcon
            />
          );
        }}
      </Form.Item>

      {/* Info sobre descuentos y matrícula */}
      <div className="space-y-2">
        <div className="text-center p-3 bg-blue-50 rounded">
          <Text type="secondary" className="text-xs">
            💡 Los descuentos automáticos se aplicarán según las reglas del sistema
          </Text>
        </div>

        <div className="text-center p-3 bg-purple-50 rounded">
          <Text type="secondary" className="text-xs">
            🎫 Si el recinto tiene matrícula configurada y el taller la requiere, se aplicará automáticamente al momento de la inscripción
          </Text>
        </div>
      </div>
    </div>
  );
}
